/**
 * Managed SQL Database Type Definitions
 *
 * TypeScript types for managed SQL database entities and operations.
 * Mirrors backend models from tenant-api/src/models/index.ts
 *
 * Managed SQL databases are PostgreSQL databases in a shared Aurora cluster
 * with IAM-enforced access control per tenant.
 */

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Managed SQL Database status values
 */
export const ManagedSqlDbStatus = {
  CREATING: 'creating',
  AVAILABLE: 'available',
  UPDATING: 'updating',
  DELETING: 'deleting',
  FAILED: 'failed',
} as const;

export type ManagedSqlDbStatus =
  (typeof ManagedSqlDbStatus)[keyof typeof ManagedSqlDbStatus];

// ============================================================================
// MANAGED SQL DATABASE ENTITY
// ============================================================================

/**
 * Managed SQL Database entity
 */
export interface ManagedSqlDb {
  tenantId: string;
  managedSqlDbId: string; // UUID v4
  managedSqlDbCode: string; // Unique immutable code within tenant
  managedSqlDbName: string; // Display name
  description?: string;
  databaseName: string; // Actual PostgreSQL database name
  status: ManagedSqlDbStatus;
  statusMessage?: string; // Error details if status is FAILED
  createdBy: string; // User ID
  entityType: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Sanitized ManagedSqlDb (for API responses)
 * Same as ManagedSqlDb since dbUsername is excluded server-side
 */
export type ManagedSqlDbSanitized = ManagedSqlDb;

// ============================================================================
// DTOs
// ============================================================================

/**
 * DTO for managed SQL database creation
 */
export interface ManagedSqlDbCreateDTO {
  managedSqlDbCode: string; // Unique code within tenant
  managedSqlDbName: string; // Display name
  description?: string;
}

/**
 * DTO for managed SQL database updates
 * Note: managedSqlDbCode is immutable and cannot be updated
 */
export interface ManagedSqlDbUpdateDTO {
  managedSqlDbName?: string;
  description?: string;
}

// ============================================================================
// QUERY TYPES
// ============================================================================

/**
 * Managed SQL database list query parameters
 */
export interface ManagedSqlDbListQuery {
  status?: ManagedSqlDbStatus;
  limit?: number;
  lastKey?: string;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

/**
 * Managed SQL database list response
 * Note: Backend uses "databases" field name
 */
export interface ManagedSqlDbListResponse {
  databases: ManagedSqlDbSanitized[];
  count: number;
  lastEvaluatedKey?: string;
}
